import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const ModernRegister = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { completeLogin } = useAuth();

  const passwordRequirements = [
    { id: 'length', label: 'At least 8 characters', test: pwd => pwd.length >= 8 },
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!passwordRequirements.every(req => req.test(formData.password))) {
      newErrors.password = 'Password does not meet requirements';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.auth.register({
        username: formData.username,
        email: formData.email,
        password: formData.password,
      });

      if (response.data.token) {
        const signedIn = await completeLogin(response.data.token, formData.username);
        navigate(signedIn ? '/dashboard' : '/login', {
          replace: true,
          state: { message: 'Account created. Sign in to continue.' },
        });
        return;
      }

      navigate('/login', {
        state: {
          message: response.data.detail,
        },
      });
    } catch (err) {
      setErrors({
        ...Object.fromEntries(
          Object.entries(err.response?.data || {})
            .filter(([key]) => ['username', 'email', 'password'].includes(key))
            .map(([key, value]) => [key, Array.isArray(value) ? value.join(' ') : value])
        ),
        general:
          err.response?.data?.detail ||
          err.response?.data?.message ||
          'Registration failed. Please check the fields and try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="account-page">
      <div className="w-full">
        <div>
          {/* Header */}
          <div className="account-header">
            <h1 className="account-title">Create your account</h1>
            <p className="account-description">
              An account lets you save BLAST searches. Public data and BLAST are open to everyone.
              Private lab data requires access granted by a lab administrator.
            </p>
          </div>

          {/* Form Card */}
          <div className="max-w-md mx-auto">
            <div className="account-form">
              {errors.general && (
                <div className="alert-error mb-6" role="alert">
                  <p className="text-sm font-medium">{errors.general}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6" aria-busy={loading}>
                <div>
                  <label
                    htmlFor="username"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Username
                  </label>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    autoComplete="username"
                    autoCapitalize="none"
                    spellCheck={false}
                    aria-invalid={Boolean(errors.username)}
                    aria-describedby={errors.username ? 'username-error' : undefined}
                    required
                    value={formData.username}
                    onChange={handleChange}
                    className={`input ${errors.username ? 'input-error' : ''}`}
                    placeholder="Choose a username"
                  />
                  {errors.username && (
                    <p id="username-error" role="alert" className="mt-2 text-sm text-error-700">
                      {errors.username}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={errors.email ? 'email-error' : undefined}
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className={`input ${errors.email ? 'input-error' : ''}`}
                    placeholder="you@example.com"
                  />
                  {errors.email && (
                    <p id="email-error" role="alert" className="mt-2 text-sm text-error-700">
                      {errors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    aria-invalid={Boolean(errors.password)}
                    aria-describedby={`password-requirements${
                      errors.password ? ' password-error' : ''
                    }`}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className={`input ${errors.password ? 'input-error' : ''}`}
                    placeholder="Create a password"
                  />

                  {/* Password requirements */}
                  <ul
                    id="password-requirements"
                    className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2"
                  >
                    {passwordRequirements.map(req => (
                      <li key={req.id} className="flex items-start gap-2">
                        <span aria-hidden="true" className="text-sm text-gray-600">
                          {req.test(formData.password) ? '✓' : '○'}
                        </span>
                        <span
                          className={`text-xs ${
                            formData.password && req.test(formData.password)
                              ? 'text-success-700 font-medium'
                              : 'text-gray-600'
                          }`}
                        >
                          {req.label}
                          <span className="sr-only">
                            {req.test(formData.password) ? ': met' : ': not met'}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                  {errors.password && (
                    <p id="password-error" role="alert" className="mt-2 text-sm text-error-700">
                      {errors.password}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Confirm password
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    aria-invalid={Boolean(errors.confirmPassword)}
                    aria-describedby={errors.confirmPassword ? 'confirm-password-error' : undefined}
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`input ${errors.confirmPassword ? 'input-error' : ''}`}
                    placeholder="Confirm your password"
                  />
                  {errors.confirmPassword && (
                    <p
                      id="confirm-password-error"
                      role="alert"
                      className="mt-2 text-sm text-error-700"
                    >
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <button type="submit" disabled={loading} className="btn-primary btn-lg w-full">
                    {loading ? (
                      <span className="flex items-center justify-center">
                        <span className="spinner mr-2" aria-hidden="true"></span>
                        Creating account...
                      </span>
                    ) : (
                      'Create account'
                    )}
                  </button>
                </div>
              </form>
              <Link to="/verify-email" className="workspace-link mt-3">
                Resend verification email
              </Link>
            </div>

            {/* Sign in link */}
            <div className="text-center mt-6">
              <p className="text-sm text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="workspace-link">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernRegister;
